import { useState } from "react";
import PhotoPlaceholder from "./PhotoPlaceholder.jsx";
import { Link } from "react-router-dom";
import { useApp } from "../app/AppContext.jsx";
import { money, km, carTitle, marketLabel } from "../lib/format.js";
import { specLabel } from "../i18n/index.js";

/**
 * A card shows the *landed* price in the visitor's country, not the Korean or Japanese
 * asking price — that is the whole promise of the portal, so the number that decides a
 * click has to be the number the buyer actually pays.
 *
 * `quote` is optional: the grid renders instantly from the listing and fills prices in as
 * the batch quote resolves, rather than blocking the page on arithmetic.
 */
export default function CarCard({ listing: l, quote, priority = false }) {
  const { t, country, currency, lang } = useApp();
  const [failedImage, setFailedImage] = useState(null);
  const img = l.images?.[0] !== failedImage ? l.images?.[0] : null;
  const title = carTitle(l);

  return (
    <Link to={`/car/${encodeURIComponent(l.id)}`} className="card">
      {/* Above-the-fold cards load eagerly: `loading="lazy"` on the first row defers the very
          image that is the page's largest contentful paint, so the grid renders as a wall of
          empty boxes and then fills in. */}
      <div className={`card__media${img ? "" : " card__media--empty"}`}>
        {img ? (
          <img
            src={img} alt={title}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            onError={() => setFailedImage(img)}
          />
        ) : (
          <PhotoPlaceholder />
        )}
        <div className="card__badges">
          <span className="badge badge--plain">{marketLabel(l.source, t)}</span>
          {l.steering === "LHD" && <span className="badge badge--ok">LHD</span>}
          {l.steering === "RHD" && <span className="badge badge--warn">RHD</span>}
          {l.isOriginating && <span className="badge badge--gold">FTA</span>}
          {/* A demo lot has to say so on the card too — cards get linked and shared. */}
          {l.isDemo && <span className="badge badge--warn">{t("car.demoLot")}</span>}
          {/* Cars this country cannot register are not in the grid at all. The ones that are
              merely difficult are, and the card is where that has to be said — a buyer who
              finds out on the enquiry reply has already spent an afternoon on it. */}
          {l.importability === "hard" && (
            <span className="badge badge--warn">{t("car.import.hard", { country })}</span>
          )}
        </div>
        {l.imageCount > 1 && <span className="card__count">{l.imageCount} ⬚</span>}
      </div>

      <div className="card__body">
        <h3 className="card__title">{title}</h3>

        <div className="card__specs">
          {l.year && <span>{l.year}</span>}
          {l.mileageKm != null && <span>{km(l.mileageKm, lang)}</span>}
          {l.fuel && <span>{specLabel(lang, "fuel", l.fuel)}</span>}
          {l.transmission && <span>{specLabel(lang, "transmission", l.transmission)}</span>}
        </div>

        <div className="card__price">
          {quote?.total ? (
            <>
              {/* One number, not a range.
                  On a grid the buyer is deciding whether to click, and "EUR 183,840 –
                  251,680" answers nothing. Fixed costs (approval, freight, sourcing) carry
                  genuine ranges that dominate a cheap car proportionally, so the band is real
                  — but it belongs on the detail page next to the line items that explain it. */}
              <div className="card__landed">{money(quote.total.typical, quote.currency || currency, country)}</div>
              <div className="card__origin-price">
                {t("car.landedIn", { country })} · {t("common.estimate").toLowerCase()}
              </div>
            </>
          ) : l.priceEur != null ? (
            <>
              <div className="card__landed">{money(l.priceEur, "EUR", country)}</div>
              <div className="card__origin-price">
                {t("car.listedAt", { market: marketLabel(l.source, t) })}
              </div>
            </>
          ) : (
            <>
              <div className="card__landed" style={{ fontSize: 16 }}>{t("car.priceOnRequest")}</div>
              <div className="card__origin-price">{marketLabel(l.source, t)}</div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export function CarCardSkeleton() {
  return (
    <div className="card">
      <div className="card__media skeleton" />
      <div className="card__body">
        <div className="skeleton" style={{ height: 18, width: "80%" }} />
        <div className="skeleton" style={{ height: 13, width: "55%" }} />
        <div className="card__price">
          <div className="skeleton" style={{ height: 22, width: "65%" }} />
        </div>
      </div>
    </div>
  );
}
